<template>
  <div
    v-if="showNoticeArea"
    :id="pageNavigationIds.NOTICE"
    class="items-show__item-notice-wrapper"
  >
    <div class="items-show__item-notice-content">
      <div class="notice-area">
        <DecorationHead v-if="showTitle" text="注意事項" :heading-level="2" />
        <div class="notice-area__body">
          <p v-if="showKeyWarnings" class="notice-area__key-warnings">
            <TextWithLinks
              :text="noticeDetail.keyWarnings"
              link-class="notice-area__link"
            />
          </p>

          <p v-if="showGeneralNotes" class="notice-area__text">
            <TextWithLinks
              :text="noticeDetail.generalNotes"
              link-class="notice-area__link"
            />
          </p>

          <div v-if="showUserRestriction" class="notice-area__item">
            <HeadingFour>対象者・対象年齢</HeadingFour>
            <p class="notice-area__text">
              <TextWithLinks
                :text="noticeDetail.userRestrictionDescription"
                link-class="notice-area__link"
              />
            </p>
          </div>

          <div v-if="showStoreRestriction" class="notice-area__item">
            <HeadingFour>店舗情報</HeadingFour>
            <p class="notice-area__text">
              <TextWithLinks
                :text="noticeDetail.storeRestrictionDescription"
                link-class="notice-area__link"
              />
            </p>
          </div>

          <div v-if="showRegularHoliday" class="notice-area__item">
            <HeadingFour>定休日</HeadingFour>
            <p class="notice-area__text">
              <TextWithLinks
                :text="noticeDetail.regularHoliday"
                link-class="notice-area__link"
              />
            </p>
          </div>

          <div v-if="showDeliveryEstimate" class="notice-area__item">
            <HeadingFour>発送までの目安日数</HeadingFour>
            <p v-if="!isDeliveryEstimatePending" class="notice-area__text">
              <TextWithLinks
                v-if="isDeliveryEstimateOthers"
                :text="noticeDetail.deliveryEstimate.description"
                link-class="notice-area__link"
              />
              <span v-else>{{ deliveryEstimateTypeLabel }}</span>
            </p>
            <div
              v-if="showDeliveryHolidayPeriodDescription"
              class="notice-area__supplement"
            >
              <p class="notice-area__supplement-title">
                通常と異なるケースについて
              </p>
              <TextWithLinks
                :text="noticeDetail.deliveryEstimate.holidayPeriodDescription"
                link-class="notice-area__link"
              />
            </div>
            <p
              v-if="showGifteeSystemNote"
              class="notice-area__giftee-system-note"
            >
              ※商品発送時にはgifteeより受取主様へ、商品発送のお知らせメールが自動送信されます。
            </p>
          </div>

          <div v-if="showRefrigeratedShipping" class="notice-area__item">
            <HeadingFour>クール便（冷蔵・冷凍）の利用</HeadingFour>
            <p class="notice-area__text">
              <TextWithLinks
                v-if="isRefrigeratedShippingSeasonal"
                :text="noticeDetail.refrigeratedShipping.description"
                link-class="notice-area__link"
              />
              <span v-else>{{ refrigeratedShippingTypeLabel }}</span>
            </p>
          </div>

          <div v-if="showDeliveryRestriction" class="notice-area__item">
            <HeadingFour>配送不可地域</HeadingFour>
            <p class="notice-area__text">
              <TextWithLinks
                v-if="isDeliveryRestrictionYes"
                :text="noticeDetail.deliveryRestriction.description"
                link-class="notice-area__link"
              />
              <span v-else>ありません。</span>
            </p>
          </div>

          <div v-if="showRedelivery" class="notice-area__item">
            <HeadingFour>再配送発生時の料金負担</HeadingFour>
            <p v-if="!isRedeliveryPending" class="notice-area__text">
              <TextWithLinks
                v-if="isRedeliveryOthers"
                :text="noticeDetail.redelivery.costBearerDescription"
                link-class="notice-area__link"
              />
              <span v-else>{{ redeliveryCostBearerTypeLabel }}</span>
            </p>
            <div
              v-if="showRedeliveryDescription"
              class="notice-area__supplement"
            >
              <TextWithLinks
                :text="noticeDetail.redelivery.description"
                link-class="notice-area__link"
              />
            </div>
          </div>

          <div v-if="showMemberRegistration" class="notice-area__item">
            <HeadingFour>会員登録の要否</HeadingFour>
            <p class="notice-area__text">
              <span>登録が必要です。</span>
            </p>
            <div
              v-if="showMemberRegistrationDescription"
              class="notice-area__supplement"
            >
              <TextWithLinks
                :text="noticeDetail.memberRegistration.description"
                link-class="notice-area__link"
              />
            </div>
          </div>

          <div v-if="showCreditCardRegistration" class="notice-area__item">
            <HeadingFour>クレジットカード登録の要否</HeadingFour>
            <p class="notice-area__text">
              <span>登録が必要です。</span>
            </p>
            <div
              v-if="showCreditCardRegistrationDescription"
              class="notice-area__supplement"
            >
              <TextWithLinks
                :text="noticeDetail.creditCardRegistration.description"
                link-class="notice-area__link"
              />
            </div>
          </div>

          <div v-if="showExistingMemberUnavailable" class="notice-area__item">
            <HeadingFour>既存会員の利用可否</HeadingFour>
            <p class="notice-area__text">
              <span>ご利用できません。</span>
            </p>
            <div
              v-if="showExistingMemberUnavailableDescription"
              class="notice-area__supplement"
            >
              <TextWithLinks
                :text="noticeDetail.existingMemberUnavailable.description"
                link-class="notice-area__link"
              />
            </div>
          </div>

          <div v-if="showShippingCostByRecipient" class="notice-area__item">
            <HeadingFour>送料負担の要否</HeadingFour>
            <p class="notice-area__text">
              <span>ご負担いただきます。</span>
            </p>
            <div
              v-if="showShippingCostByRecipientDescription"
              class="notice-area__supplement"
            >
              <TextWithLinks
                :text="noticeDetail.shippingCostByRecipient.description"
                link-class="notice-area__link"
              />
            </div>
          </div>

          <div v-if="showMinimumPurchaseAmount" class="notice-area__item">
            <HeadingFour>最低利用金額の有無</HeadingFour>
            <p class="notice-area__text">
              <span>利用条件がございます。</span>
            </p>
            <div class="notice-area__supplement">
              <TextWithLinks
                :text="noticeDetail.minimumPurchaseAmount.description"
                link-class="notice-area__link"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HeadingFour from '../../atoms/headlines/HeadingFour.vue'
import TextWithLinks from '../../atoms/text/TextWithLinks.vue'
import DecorationHead from '../../atoms/decoration/DecorationHead.vue'
import { pageNavigationIds } from '../../../constants/pageNavigationConstants'

const DELIVERY_ESTIMATE_TYPE = {
  TWO_BUSINESS_DAYS_OR_LESS: 'two_business_days_or_less',
  THREE_TO_FIVE_BUSINESS_DAYS: 'three_to_five_business_days',
  SIX_TO_TEN_BUSINESS_DAYS: 'six_to_ten_business_days',
  ELEVEN_BUSINESS_DAYS_OR_MORE: 'eleven_business_days_or_more',
  OTHERS: 'others'
}

const DELIVERY_ESTIMATE_TYPE_LABELS = {
  [DELIVERY_ESTIMATE_TYPE.TWO_BUSINESS_DAYS_OR_LESS]: '2営業日以内',
  [DELIVERY_ESTIMATE_TYPE.THREE_TO_FIVE_BUSINESS_DAYS]: '3-5営業日',
  [DELIVERY_ESTIMATE_TYPE.SIX_TO_TEN_BUSINESS_DAYS]: '6-10営業日',
  [DELIVERY_ESTIMATE_TYPE.ELEVEN_BUSINESS_DAYS_OR_MORE]: '11営業日以上'
}

const REFRIGERATED_SHIPPING_TYPE = {
  ALL_YEAR: 'all_year',
  SEASONAL: 'seasonal',
  NOT_APPLICABLE: 'not_applicable'
}

const REFRIGERATED_SHIPPING_TYPE_LABELS = {
  [REFRIGERATED_SHIPPING_TYPE.ALL_YEAR]: '通年、クール便でお届けします。',
  [REFRIGERATED_SHIPPING_TYPE.NOT_APPLICABLE]: '通年、常温便でお届けします。'
}

const REDELIVERY_COST_BEARER_TYPE = {
  DELIVERY_COST_BY_COMPANY: 'delivery_cost_by_company',
  DELIVERY_COST_BY_RECIPIENT: 'delivery_cost_by_recipient',
  DELIVERY_COST_AND_PRODUCT_COST_BY_RECIPIENT:
    'delivery_cost_and_product_cost_by_recipient',
  OTHERS: 'others'
}

const REDELIVERY_COST_BEARER_TYPE_LABELS = {
  [REDELIVERY_COST_BEARER_TYPE.DELIVERY_COST_BY_COMPANY]:
    '受取主様には発生しません。ブランド側で負担いたします。',
  [REDELIVERY_COST_BEARER_TYPE.DELIVERY_COST_BY_RECIPIENT]:
    '受取主様のご都合（ご不在・住所誤り等）により返送後の再送をご希望の場合、再送料は受取主様にご負担いただきますことをご了承ください。',
  [REDELIVERY_COST_BEARER_TYPE.DELIVERY_COST_AND_PRODUCT_COST_BY_RECIPIENT]:
    '受取主様のご都合（ご不在・住所誤り等）により返送後の再送をご希望の場合、再送料と商品代金を受取主様にご負担いただきますことをご了承ください。'
}

export default {
  name: 'NoticeArea',
  components: {
    HeadingFour,
    TextWithLinks,
    DecorationHead
  },
  props: {
    noticeDetail: {
      type: Object,
      required: false,
      default: null
    },
    isShippingInfoModal: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      pageNavigationIds
    }
  },
  computed: {
    showTitle() {
      return !this.isShippingInfoModal
    },
    showKeyWarnings() {
      if (this.isShippingInfoModal) return false
      return (
        this.noticeDetail?.keyWarnings &&
        this.noticeDetail.keyWarnings.trim() !== ''
      )
    },
    showGeneralNotes() {
      if (this.isShippingInfoModal) return false
      return (
        this.noticeDetail?.generalNotes &&
        this.noticeDetail.generalNotes.trim() !== ''
      )
    },
    showUserRestriction() {
      if (this.isShippingInfoModal) return false
      return (
        this.noticeDetail?.userRestrictionDescription &&
        this.noticeDetail.userRestrictionDescription.trim() !== ''
      )
    },
    showStoreRestriction() {
      if (this.isShippingInfoModal) return false
      return (
        this.noticeDetail?.storeRestrictionDescription &&
        this.noticeDetail.storeRestrictionDescription.trim() !== ''
      )
    },
    showRegularHoliday() {
      if (this.isShippingInfoModal) return false
      return (
        this.noticeDetail?.regularHoliday &&
        this.noticeDetail.regularHoliday.trim() !== ''
      )
    },
    showDeliveryEstimate() {
      return (
        !this.isDeliveryEstimatePending ||
        this.showDeliveryHolidayPeriodDescription ||
        this.showGifteeSystemNote
      )
    },
    isDeliveryEstimatePending() {
      const estimate = this.noticeDetail?.deliveryEstimate
      return !estimate?.type || estimate.type === ''
    },
    isDeliveryEstimateOthers() {
      return (
        this.noticeDetail?.deliveryEstimate?.type ===
        DELIVERY_ESTIMATE_TYPE.OTHERS
      )
    },
    deliveryEstimateTypeLabel() {
      const type = this.noticeDetail?.deliveryEstimate?.type
      return DELIVERY_ESTIMATE_TYPE_LABELS[type] || ''
    },
    showDeliveryHolidayPeriodDescription() {
      return (
        this.noticeDetail?.deliveryEstimate?.holidayPeriodDescription &&
        this.noticeDetail.deliveryEstimate.holidayPeriodDescription.trim() !==
          ''
      )
    },
    showGifteeSystemNote() {
      return this.noticeDetail?.deliveryEstimate?.useGifteeSystem === true
    },
    showRefrigeratedShipping() {
      const refrigeratedShipping = this.noticeDetail?.refrigeratedShipping
      if (!refrigeratedShipping) return false
      return refrigeratedShipping.type && refrigeratedShipping.type !== ''
    },
    isRefrigeratedShippingSeasonal() {
      return (
        this.noticeDetail?.refrigeratedShipping?.type ===
        REFRIGERATED_SHIPPING_TYPE.SEASONAL
      )
    },
    refrigeratedShippingTypeLabel() {
      const type = this.noticeDetail?.refrigeratedShipping?.type
      return REFRIGERATED_SHIPPING_TYPE_LABELS[type] || ''
    },
    showDeliveryRestriction() {
      if (this.isShippingInfoModal) return false
      const restriction = this.noticeDetail?.deliveryRestriction
      if (!restriction) return false
      return restriction.type && restriction.type !== ''
    },
    isDeliveryRestrictionYes() {
      return this.noticeDetail?.deliveryRestriction?.type === 'yes'
    },
    showRedelivery() {
      return !this.isRedeliveryPending || this.showRedeliveryDescription
    },
    isRedeliveryPending() {
      const redelivery = this.noticeDetail?.redelivery
      return !redelivery?.costBearerType || redelivery.costBearerType === ''
    },
    isRedeliveryOthers() {
      return (
        this.noticeDetail?.redelivery?.costBearerType ===
        REDELIVERY_COST_BEARER_TYPE.OTHERS
      )
    },
    redeliveryCostBearerTypeLabel() {
      const type = this.noticeDetail?.redelivery?.costBearerType
      return REDELIVERY_COST_BEARER_TYPE_LABELS[type] || ''
    },
    showRedeliveryDescription() {
      return (
        this.noticeDetail?.redelivery?.description &&
        this.noticeDetail.redelivery.description.trim() !== ''
      )
    },
    showMemberRegistration() {
      if (this.isShippingInfoModal) return false
      return this.noticeDetail?.memberRegistration !== null
    },
    showMemberRegistrationDescription() {
      return (
        this.noticeDetail?.memberRegistration?.description &&
        this.noticeDetail.memberRegistration.description.trim() !== ''
      )
    },
    showCreditCardRegistration() {
      if (this.isShippingInfoModal) return false
      return this.noticeDetail?.creditCardRegistration !== null
    },
    showCreditCardRegistrationDescription() {
      return (
        this.noticeDetail?.creditCardRegistration?.description &&
        this.noticeDetail.creditCardRegistration.description.trim() !== ''
      )
    },
    showExistingMemberUnavailable() {
      if (this.isShippingInfoModal) return false
      return this.noticeDetail?.existingMemberUnavailable !== null
    },
    showExistingMemberUnavailableDescription() {
      return (
        this.noticeDetail?.existingMemberUnavailable?.description &&
        this.noticeDetail.existingMemberUnavailable.description.trim() !== ''
      )
    },
    showShippingCostByRecipient() {
      if (this.isShippingInfoModal) return false
      return this.noticeDetail?.shippingCostByRecipient !== null
    },
    showShippingCostByRecipientDescription() {
      return (
        this.noticeDetail?.shippingCostByRecipient?.description &&
        this.noticeDetail.shippingCostByRecipient.description.trim() !== ''
      )
    },
    showMinimumPurchaseAmount() {
      if (this.isShippingInfoModal) return false
      return this.noticeDetail?.minimumPurchaseAmount !== null
    },
    showMinimumPurchaseAmountDescription() {
      return (
        this.noticeDetail?.minimumPurchaseAmount?.description &&
        this.noticeDetail.minimumPurchaseAmount.description.trim() !== ''
      )
    },
    showNoticeArea() {
      return (
        this.showKeyWarnings ||
        this.showGeneralNotes ||
        this.showUserRestriction ||
        this.showStoreRestriction ||
        this.showRegularHoliday ||
        this.showDeliveryEstimate ||
        this.showRefrigeratedShipping ||
        this.showDeliveryRestriction ||
        this.showRedelivery ||
        this.showMemberRegistration ||
        this.showCreditCardRegistration ||
        this.showExistingMemberUnavailable ||
        this.showShippingCostByRecipient ||
        this.showMinimumPurchaseAmount
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.items-show__item-notice-wrapper {
  margin: 16px auto 0 auto;
  background-color: $white;
  text-align: left;
}

.items-show__item-notice-content {
  margin: 0 auto;
  padding-bottom: 32px;
  max-width: $base-contents-width;
}

.notice-area__body {
  width: auto;
  padding: 24px 24px 0 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media all and (max-width: ($base-contents-width - 1)) {
    padding: 24px 16px 0 16px;
  }
}

.notice-area__item {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.notice-area__key-warnings {
  @extend %body;
  color: $danger;
  background-color: rgba($danger, 0.1);
  border: 1px solid rgba($danger, 0.3);
  border-radius: 4px;
  padding: 8px;
  white-space: pre-wrap;
  line-height: 1.6;
}

.notice-area__text {
  @extend %body;
  white-space: pre-wrap;
  letter-spacing: 0.28px;
  line-height: 1.6;
}

.notice-area__link {
  color: $link;
}

.notice-area__supplement {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: $gray-1;
  background-color: $gray-3;
  border: 1px solid $gray-2;
  border-radius: 4px;
  padding: 16px;
  font-size: 1.4rem;
  line-height: 1.6;
  letter-spacing: 0.56px;
  white-space: pre-wrap;
}

.notice-area__supplement-title {
  font-weight: bold;
  white-space: nowrap;
}

.notice-area__giftee-system-note {
  @extend %body;
  color: $gray-1;
}
</style>
